import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import {
  WHY_BUY_BENEFITS,
  WHY_BUY_KEY_POINTS,
  WHY_BUY_COMPARISON,
  SELL_CAR_BENEFITS,
} from '../../lib/businessInfo';

interface WhyBuyFromUsProps {
  variant?: 'full' | 'compact' | 'seller';
  className?: string;
}

/**
 * WhyBuyFromUs - Single Source of Truth Component
 * 
 * Replaces 3 duplicate implementations:
 * - home/components/WhyBuyFromUs.tsx (full variant)
 * - vehicle-detail/components/WhyBuyFromUs.tsx (compact variant)
 * - sell-your-car/components/WhyBuyFromUs.tsx (seller variant)
 * 
 * All data sourced from businessInfo.ts
 */
export default function WhyBuyFromUs({ variant = 'full', className = '' }: WhyBuyFromUsProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const [showAll, setShowAll] = useState(false);

  // ── SELLER VARIANT (Sell Your Car Page) ──
  if (variant === 'seller') {
    return (
      <section className={`py-20 bg-[#F7F5F0] ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#1C2B1E] mb-4">
              The Smarter Way to Sell Your Car
            </h2>
            <div className="w-24 h-1 bg-[#E8B84B] mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Skip the stress of private sales, tire-kickers, and lowball dealership offers. Rockhound Auto makes selling your car simple. 
              Prefer to sell on your own timeline?{' '}
              <Link to="/consignment" className="text-[#E8B84B] hover:text-[#d4a03d] font-medium cursor-pointer">
                Check out our consignment services
              </Link>
              .
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SELL_CAR_BENEFITS.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-[#E8E2D6] hover:border-[#E8B84B] transition-all group"
              >
                <div className="w-16 h-16 bg-[#E8B84B] rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${benefit.icon} text-3xl text-[#1C2B1E]`} aria-hidden="true"></i>
                </div>
                <h3 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {'linkText' in benefit && benefit.linkText && benefit.linkUrl ? (
                    <>
                      {benefit.description.replace(benefit.linkText, '')}
                      <Link to={benefit.linkUrl} className="text-[#E8B84B] hover:text-[#d4a03d] font-medium cursor-pointer">
                        {benefit.linkText}
                      </Link>
                      .
                    </>
                  ) : (
                    benefit.description
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── COMPACT VARIANT (Vehicle Detail Page) ──
  if (variant === 'compact') {
    const compactBenefits = [
      WHY_BUY_BENEFITS[0], // $250 Flat Doc Fee
      WHY_BUY_BENEFITS[1], // Inspected Before You See It
      WHY_BUY_BENEFITS[6], // Honest Condition Disclosure
      WHY_BUY_BENEFITS[5], // Your Purchase Saves a Dog
      WHY_BUY_BENEFITS[7], // Full Transparency Upfront
      WHY_BUY_BENEFITS[4], // We Come to You
    ];

    return (
      <div className={`bg-[#F7F5F0] rounded-lg border border-[#E8E2D6] p-8 ${className}`}>
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-1 h-8 bg-[#E8B84B] rounded-full"></div>
            <h2 className="text-3xl font-bold text-[#1C2B1E] font-['Oswald']">Why Buy From Us</h2>
            <div className="w-1 h-8 bg-[#E8B84B] rounded-full"></div>
          </div>
          <p className="text-[#1C2B1E]/70 max-w-2xl mx-auto">
            We're not your typical dealership. Here's what makes us different.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {compactBenefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-lg border border-[#E8E2D6] p-6 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-[#E8B84B]/10 rounded-full flex items-center justify-center mb-4">
                <i className={`${benefit.icon} text-2xl text-[#7a5208]`} aria-hidden="true"></i>
              </div>
              <h3 className="font-['Oswald'] text-xl font-semibold text-[#1C2B1E] mb-2">
                {benefit.title}
              </h3>
              <p className="text-[#1C2B1E]/70 text-sm leading-relaxed">
                {benefit.description.replace(/<a[^>]*>.*?<\/a>/g, '').replace(/\s+/g, ' ').trim()}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── FULL VARIANT (Homepage) ──
  return (
    <section ref={ref} className={`py-20 bg-white ${className}`} aria-label="Why buy from Rockhound Auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <span className="inline-block px-3 py-1 bg-[#E8B84B]/10 border border-[#E8B84B]/30 text-[#7a5208] text-xs font-bold uppercase tracking-widest rounded-full mb-4">
            Why It Matters to You
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
            The Rockhound Difference
          </h2>
          <p className="text-base text-[#4A5568] max-w-2xl mx-auto">
            Not marketing fluff — real, measurable differences that save you money, time, and stress.
          </p>
        </div>

        {/* Key Points - Prominent Feature */}
        <div
          className="mb-16 transition-all duration-700 delay-75"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_BUY_KEY_POINTS.map((point, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-[#1A2332] to-[#2C3E55] rounded-xl p-6 text-center hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#E8B84B] rounded-full mx-auto mb-4">
                  <i className={`${point.icon} text-2xl text-[#1A2332]`} aria-hidden="true"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {point.title}
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">

          {/* Left: Comparison Table */}
          <div
            className="transition-all duration-700 delay-100"
            style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0)' : 'translateX(-20px)' }}
          >
            <div className="bg-[#F7F5F0] rounded-2xl border border-[#E8E2D6] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[320px]" aria-label="Rockhound Auto vs typical dealer comparison">
                  <thead>
                    <tr className="bg-[#1A2332] text-white text-xs font-bold uppercase tracking-wider">
                      <th scope="col" className="px-3 py-3 text-left text-white">Feature</th>
                      <th scope="col" className="px-3 py-3 text-center text-[#E8B84B]">Rockhound</th>
                      <th scope="col" className="px-3 py-3 text-center text-white/80">Typical Dealer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {WHY_BUY_COMPARISON.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-b border-[#E8E2D6] last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F7F5F0]'}`}
                      >
                        <th scope="row" className="px-3 py-3 text-xs font-semibold text-[#1A2332] text-left">{row.label}</th>
                        <td className="px-3 py-3 text-center">
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#2A7A5A]">
                            <i className="ri-check-line text-sm" aria-hidden="true"></i>
                            {row.us}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-xs text-[#4A5568]">{row.them}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/compare"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1A2332] text-[#E8B84B] rounded-xl font-bold text-sm hover:bg-[#2C3E55] transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
              >
                <i className="ri-bar-chart-grouped-line" aria-hidden="true"></i>
                Full Comparison
              </Link>
              <Link
                to="/how-it-works"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-[#E8E2D6] text-[#1A2332] rounded-xl font-bold text-sm hover:border-[#E8B84B] hover:text-[#E8B84B] transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B]"
              >
                <i className="ri-question-line" aria-hidden="true"></i>
                How It Works
              </Link>
            </div>
          </div>

          {/* Right: Benefit Cards */}
          <div
            className="transition-all duration-700 delay-200"
            style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0)' : 'translateX(20px)' }}
          >
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0">
              {(showAll ? WHY_BUY_BENEFITS : WHY_BUY_BENEFITS.slice(0, 4)).map((b, i) => (
                <li
                  key={i}
                  className="bg-[#F7F5F0] rounded-xl border border-[#E8E2D6] p-5 hover:border-[#E8B84B] hover:shadow-md transition-all duration-200 flex flex-col"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-[#E8E2D6] mb-3 flex-shrink-0" aria-hidden="true">
                    <i className={`${b.icon} text-xl text-[#7a5208]`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-[#1A2332] mb-1.5" style={{ fontFamily: 'Oswald, sans-serif', fontSize: '1rem' }}>
                    {b.title}
                  </h3>
                  <p className="text-xs text-[#374151] leading-relaxed flex-1" dangerouslySetInnerHTML={{ __html: b.description }}></p>
                  {b.link && b.linkLabel && (
                    <Link
                      to={b.link}
                      className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[#7a5208] hover:text-[#1A2332] transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:rounded"
                    >
                      {b.linkLabel} <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {!showAll && (
              <button
                onClick={() => setShowAll(true)}
                aria-label={`Show ${WHY_BUY_BENEFITS.length - 4} more reasons to buy from Rockhound`}
                className="mt-4 w-full py-3 border-2 border-dashed border-[#E8E2D6] rounded-xl text-sm font-semibold text-[#4A5568] hover:border-[#E8B84B] hover:text-[#E8B84B] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B]"
              >
                Show {WHY_BUY_BENEFITS.length - 4} more reasons <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}